/**
 * Formats a given date into a human-readable relative time string (e.g., "2 minutes ago", "in 3 days")
 * using the Persian (`fa-IR`) locale.
 *
 * The function calculates the time difference between the provided date and the current time,
 * then returns a localized relative time string in the largest appropriate unit
 * (seconds, minutes, hours, days, weeks, months, or years).
 *
 * @param {Date} date - The target date to compare with the current time.
 * @returns {string} A localized relative time string in Persian indicating the time difference.
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 60000));
 * // "۱ دقیقه پیش"
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 2400000));
 * // "۴ ساعت پیش"
 */

export const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const difference = Math.floor(
    (new Date(date).getTime() - now.getTime()) / 1000,
  );

  const absoluteDifference = Math.abs(difference);

  const relativeTimeFormat = new Intl.RelativeTimeFormat('fa-IR', {
    numeric: 'auto',
  });

  if (absoluteDifference < 60) {
    return relativeTimeFormat.format(difference, 'second');
  }
  if (absoluteDifference < 3600) {
    return relativeTimeFormat.format(Math.floor(difference / 60), 'minute');
  }
  if (absoluteDifference < 86400) {
    return relativeTimeFormat.format(Math.floor(difference / 3600), 'hour');
  }
  if (absoluteDifference < 604800) {
    return relativeTimeFormat.format(Math.floor(difference / 86400), 'day');
  }
  if (absoluteDifference < 2629800) {
    return relativeTimeFormat.format(Math.floor(difference / 604800), 'week');
  }
  if (absoluteDifference < 31557600) {
    return relativeTimeFormat.format(Math.floor(difference / 2629800), 'month');
  }

  return relativeTimeFormat.format(Math.floor(difference / 31557600), 'year');
};
